import React, { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { planService } from '@/services/planService'
import { useToast } from '@/contexts/ToastContext'
import { Plan } from '@/types/plan'

interface EditPlanModalProps {
  plan: Plan
  onClose: () => void
  onPlanUpdated: (plan: Plan) => void
}

const toDateInputValue = (iso: string): string => iso.slice(0, 10)

const EditPlanModal: React.FC<EditPlanModalProps> = ({ plan, onClose, onPlanUpdated }) => {
  const { showToast } = useToast()

  const [title, setTitle] = useState(plan.title)
  const [description, setDescription] = useState(plan.description)
  const [startDate, setStartDate] = useState(toDateInputValue(plan.startDate))
  const [endDate, setEndDate] = useState(toDateInputValue(plan.endDate))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {}
    if (!title.trim()) {
      nextErrors.title = 'Title is required'
    }
    if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
      nextErrors.endDate = 'Start date must be before end date'
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!validate()) return

    try {
      setIsSubmitting(true)
      const updated = await planService.updatePlan(plan.id, {
        title: title.trim(),
        description: description.trim(),
        startDate,
        endDate
      })
      showToast({ type: 'success', message: 'Plan updated' })
      onPlanUpdated(updated)
      onClose()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to update plan'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-xl font-semibold text-gray-900">Edit Plan</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div>
            <label htmlFor="edit-plan-title" className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              id="edit-plan-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          <div>
            <label htmlFor="edit-plan-description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="edit-plan-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="edit-plan-start-date" className="block text-sm font-medium text-gray-700 mb-1">
                Start date *
              </label>
              <input
                id="edit-plan-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="edit-plan-end-date" className="block text-sm font-medium text-gray-700 mb-1">
                End date *
              </label>
              <input
                id="edit-plan-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default EditPlanModal
